import { ColumnModel } from "target_wasm";
import { ColumnItem } from "./ColumnListElement";

interface BuildColumnItemsOptions {
  /** Columns the table is keyed on, from its `RowIdentity`. A connector table
   *  keyed by a column the source never declared still shows the key badge —
   *  the identity is what the sync matches on, the intent only what the source
   *  said. */
  keyColumnIds?: Set<string>;
  linkedColumnIds?: Set<string>;
  filteredColumnIds?: Set<string>;
  isInView?: boolean;
  isDeleted?: boolean;
  sort?: "name" | "id";
}

/** `dependency_type` is the serde-serialized `ColumnDependencyType`: the string
 *  `"Literal"` for plain columns, or `{"Computed":{…}}` for formula columns. */
function isComputedDependency(dependencyType: string): boolean {
  try {
    const parsed = JSON.parse(dependencyType);
    return (
      typeof parsed === "object" && parsed !== null && "Computed" in parsed
    );
  } catch {
    return false;
  }
}

export function compareColumnsById<T extends { id: string }>(
  a: T,
  b: T,
): number {
  return a.id.localeCompare(b.id);
}

export function buildColumnItems(
  columns: ColumnModel[],
  opts: BuildColumnItemsOptions = {},
): ColumnItem[] {
  const ordered =
    opts.sort === "id" ? [...columns].sort(compareColumnsById) : columns;

  const items: ColumnItem[] = ordered.map((col) => ({
    dataType: col.data_type,
    name: col.name,
    columnId: col.id,
    lockedType: opts.isDeleted ? false : col.is_type_locked,
    isLinked: opts.linkedColumnIds?.has(col.id),
    isFiltered: opts.filteredColumnIds?.has(col.id),
    isColumnEmpty: col.is_empty,
    isDeleted: opts.isDeleted,
    isInView: opts.isInView,
    displayTimezone: col.display_timezone,
    decimalPlaces: col.decimal_places,
    thousandSeparator: col.thousand_separator,
    timestampFormat: col.timestamp_format,
    dateFormat: col.date_format,
    durationDisplayUnit: col.duration_display_unit,
    // Optional — picker UI reads this in edit mode. Older WASM bundles
    // before T11.4 won't expose it; `undefined` is the safe default.
    unitAssignment: (col as { unit_assignment?: ColumnItem["unitAssignment"] })
      .unit_assignment,
    isNullable: col.is_nullable,
    isDistinct: col.is_distinct,
    isPrimaryKeyMember:
      col.is_primary_key_member || (opts.keyColumnIds?.has(col.id) ?? false),
    isExact: col.is_exact,
    // Optional for the same reason as `unitAssignment` — a WASM bundle predating
    // column descriptions won't expose it, and `undefined` reads as "no
    // description", which is the correct fallback.
    hasDescription: (col as { has_description?: boolean }).has_description,
    isComputed: isComputedDependency(col.dependency_type),
    linkEnabled: col.link_enabled,
  }));

  if (opts.sort === "name") {
    items.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }

  return items;
}
